import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Search } from "lucide-react";
import useApi from "../CustomHooks/useApi";

const AepsReports = () => {
  const mid = useSelector((state) => state.auth.employeeDetails?.username);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { callApi: AepsTxn } = useApi("/itpay/aeps/backend/report/aeps-txn", "GET");

  // Helper function to format date and time separately
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === "-") {
      return { date: "-", time: "-" };
    }
    
    try {
      const dateObj = new Date(dateTimeString);
      const date = dateObj.toLocaleDateString('en-IN'); // DD/MM/YYYY format
      const time = dateObj.toLocaleTimeString('en-IN', { 
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return { date, time };
    } catch (error) {
      return { date: "-", time: "-" };
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const query = `?mid=${mid}&fromDate=${fromDate}&toDate=${toDate}`;
      const response = await AepsTxn(null, query);
      
      // Extract and process the data with hierarchy information
      const extractedData = response.map((item) => {
        const userHierarchy = item?.userHighLevelRolesResponse?.userHierarchyResponse;
        let d = "-";
        let md = "-";
        let asm = "-";
        let sh = "-";
        let rm = "-";

        // Extract hierarchy information
        let currentParent = userHierarchy?.parent;
        while (currentParent) {
          switch (currentParent?.role) {
            case "DISTRIBUTOR":
              d = currentParent?.name || "-";
              break;
            case "MASTER_DISTRIBUTOR":
              md = currentParent?.name || "-";
              break;
            case "ASM":
              asm = currentParent?.name || "-";
              break;
            case "STATE_HEAD":
              sh = currentParent?.name || "-";
              break;
            case "REGIONAL_MANAGER":
              rm = currentParent?.name || "-";
              break;
          }
          currentParent = currentParent?.parent || null;
        }

        // Format created_at and updated_at
        const createdDateTime = formatDateTime(item.created_at);
        const updatedDateTime = formatDateTime(item.updated_at);

        return {
          // Transaction details
          txnId: item.txnId || "-",
          mobileNumber: item.mobileNumber || "-",
          created_at: item.created_at || "-",
          updated_at: item.updated_at || "-",
          // Separate date and time fields
          created_date: createdDateTime.date,
          created_time: createdDateTime.time,
          updated_date: updatedDateTime.date,
          updated_time: updatedDateTime.time,
          aadhaarNumber: item.aadhaarNumber || "-",
          externalTxnId: item.externalTxnId || "-",
          bankRRN: item.bankRRN || "-",
          merchantId: item.merchantId || "-",
          amount: item.amount || 0,
          txnmode: item.txn_Mode || 0,
          bank_name: item.bank_name || "-",
          status: item.status || "-",
          description: item.description || "-",
          txn_Mode: item.txn_Mode || "-",
          
          // User details
          name: userHierarchy?.name || "-",
          userId: item?.userHighLevelRolesResponse?.userId || "-",
          role: userHierarchy?.role || "-",
          
          // Hierarchy details
          d,
          md,
          asm,
          sh,
          rm,
        };
      });

      const sortedData = extractedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setHistory(sortedData);
    } catch (error) {
      console.error("Error fetching AEPS history:", error);
      alert("Failed to load AEPS history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const downloadExcel = () => {
    const headers = [
      "Transaction ID", "Mobile Number", "Created Date", "Created Time", "Updated Date", "Updated Time", 
      "Aadhaar Number", "External Txn ID", "Bank RRN", "Merchant ID", "Amount", "Bank Name", "Status", 
      "Description", "Txn Mode", "Name", "User ID", "Role", "Distributor", 
      "Master Distributor", "ASM", "State Head", "Regional Manager"
    ];
    
    const formattedHistory = history.map((record) => ({
      "Transaction ID": record.txnId,
      "Mobile Number": record.mobileNumber,
      "Created Date": record.created_date,
      "Created Time": record.created_time,
      "Updated Date": record.updated_date,
      "Updated Time": record.updated_time,
      "Aadhaar Number": record.aadhaarNumber,
      "External Txn ID": record.externalTxnId,
      "Bank RRN": record.bankRRN,
      "Merchant ID": record.merchantId,
      "Amount": record.amount,
      "Bank Name": record.bank_name,
      "Status": record.status,
      "Description": record.description,
      "Txn Mode": record.txn_Mode,
      "Name": record.name,
      "User ID": record.userId,
      "Role": record.role,
      "Distributor": record.d,
      "Master Distributor": record.md,
      "ASM": record.asm,
      "State Head": record.sh,
      "Regional Manager": record.rm,
    }));
    
    const ws = XLSX.utils.json_to_sheet(formattedHistory, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AEPS Transactions");
    XLSX.writeFile(wb, "aeps_transaction_history.xlsx");
  };

  const filteredRecords = history.filter((record) =>
    Object.values(record).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">AEPS Reports</h1>
              <p className="mt-1 text-gray-500">View and download AEPS transaction history with hierarchy details</p>
            </div>
            <button
              onClick={downloadExcel}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Excel
            </button>
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <input
              type="date"
              className="border px-4 py-2 rounded-md w-full md:w-1/4"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <input
              type="date"
              className="border px-4 py-2 rounded-md w-full md:w-1/4"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <button
              onClick={fetchHistory}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
            >
              Search
            </button>
          </div>

          <div className="mt-4">
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by ID, mobile, name or any field"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-separate border-spacing-0">
              <thead>
                <tr className="bg-teal-600 text-white text-left text-xs sticky top-0 z-10">
                  <th className="px-4 py-3 font-semibold rounded-tl-2xl">Created Date</th>
                  <th className="px-4 py-3 font-semibold">Created Time</th>
                  <th className="px-4 py-3 font-semibold">Transaction ID</th>
                  <th className="px-4 py-3 font-semibold">Mobile Number</th>
                  <th className="px-4 py-3 font-semibold">Aadhaar Number</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Txn Mode</th>
                  <th className="px-4 py-3 font-semibold">Bank Name</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Updated Date</th>
                  <th className="px-4 py-3 font-semibold">Updated Time</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Distributor</th>
                  <th className="px-4 py-3 font-semibold">Master Distributor</th>
                  <th className="px-4 py-3 font-semibold">ASM</th>
                  <th className="px-4 py-3 font-semibold">State Head</th>
                  <th className="px-4 py-3 font-semibold rounded-tr-2xl">Regional Manager</th>
                </tr>
              </thead>
              <tbody>
                {isHistoryLoading ? (
                  <tr>
                    <td colSpan="18" className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                        <span className="ml-3 text-gray-600">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentRecords.length > 0 ? (
                  currentRecords.map((record, index) => (
                    <tr
                      key={index}
                      className={`transition duration-150 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-teal-50`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-xs border-b border-gray-200">
                        {record.created_date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs border-b border-gray-200">
                        {record.created_time}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 text-xs border-b border-gray-200">
                        {record.txnId}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.mobileNumber}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.aadhaarNumber}
                      </td>
                      <td className="px-4 py-3 font-medium text-xs border-b border-gray-200">
                        ₹{record.amount}
                      </td>
                       <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.txn_Mode}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.bank_name}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            record.status === "SUCCESS"
                              ? "bg-green-100 text-green-800"
                              : record.status === "PENDING"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {record.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs border-b border-gray-200">
                        {record.updated_date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs border-b border-gray-200">
                        {record.updated_time}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.name}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.role}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.d}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.md}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.asm}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.sh}
                      </td>
                      <td className="px-4 py-3 text-xs border-b border-gray-200">
                        {record.rm}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="18" className="text-center py-8">
                      <p className="text-gray-500 text-base">No Records Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {history.length > 10 && (
            <div className="border-t border-gray-200 px-4 py-4 sm:px-6 bg-white rounded-b-2xl">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-teal-600 text-white hover:bg-teal-700"
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="text-sm text-gray-700">
                  Showing {recordsPerPage} records per page | Total {filteredRecords.length} results
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AepsReports;